import type { CSSProperties } from 'react';
import { StatusType } from './statusType';

export interface StatusPillColors {
  backgroundColor: string;
  color: string;
}

export const shipmentStatusPillColors: Record<StatusType, StatusPillColors> = {
  0: { backgroundColor: 'var(--danger-thin)', color: 'var(--danger)' },
  1: { backgroundColor: 'var(--light-warning)', color: 'var(--warning)' },
  2: { backgroundColor: 'var(--thin-green)', color: 'var(--green-900)' },
  3: { backgroundColor: 'var(--thin-green)', color: 'var(--green-900)' },
  4: { backgroundColor: 'var(--danger-thin)', color: 'var(--danger)' },
  5: { backgroundColor: 'var(--light-gray)', color: 'var(--text-secondary)' },
  6: { backgroundColor: 'var(--info-thin)', color: 'var(--info)' },
  7: { backgroundColor: 'var(--light-gray)', color: 'var(--text-light)' },
};

export const neutralStatusPillColors: StatusPillColors = {
  backgroundColor: 'var(--light-gray)',
  color: 'var(--text-light)',
};

const customerLegStatusPillColors: Record<string, StatusPillColors> = {
  INCOMPLETE: {
    backgroundColor: 'var(--customerleg-orange-thin)',
    color: 'var(--customerleg-orange)',
  },
  PENDING: { backgroundColor: 'var(--light-warning)', color: 'var(--warning)' },
  RATED: neutralStatusPillColors,
  BOOKED: {
    backgroundColor: 'var(--customerleg-purple-thin)',
    color: 'var(--customerleg-purple)',
  },
  IN_TRANSIT: { backgroundColor: 'var(--info-thin)', color: 'var(--info)' },
  DELIVERED: {
    backgroundColor: 'var(--thin-green)',
    color: 'var(--green-900)',
  },
  CANCELLED: {
    backgroundColor: 'var(--light-gray)',
    color: 'var(--text-secondary)',
  },
};

const normalizeCustomerLegStatus = (status?: string | null): string =>
  String(status ?? '')
    .trim()
    .toUpperCase()
    .replace(/[\s-]+/g, '_');

export const getCustomerLegStatusPillColors = (
  status?: string | null
): StatusPillColors | undefined =>
  customerLegStatusPillColors[normalizeCustomerLegStatus(status)];

export const isCustomerLegTerminalStatus = (
  status?: string | null
): boolean => {
  const normalizedStatus = normalizeCustomerLegStatus(status);
  return normalizedStatus === 'DELIVERED' || normalizedStatus === 'CANCELLED';
};

export const lateDeliveryPillColors: StatusPillColors = {
  backgroundColor: 'var(--danger-thin)',
  color: 'var(--danger)',
};

export const earlyDeliveryPillColors: StatusPillColors = {
  backgroundColor: 'var(--info-thin)',
  color: 'var(--info)',
};

export const deliveredDeliveryPillColors: StatusPillColors = {
  backgroundColor: 'var(--thin-green)',
  color: 'var(--green-900)',
};

export const getShipmentStatusPillColors = (
  type?: StatusType | number | string | null
): StatusPillColors => {
  const normalizedType = Number(type) as StatusType;
  return shipmentStatusPillColors[normalizedType] ?? neutralStatusPillColors;
};

export const getDeliveryStatusPillColors = (
  status?: string | null
): StatusPillColors => {
  const normalized = status?.toLowerCase() ?? '';

  if (normalized.includes('on time') || normalized === 'delivered') {
    return deliveredDeliveryPillColors;
  }

  if (normalized.includes('late')) {
    return lateDeliveryPillColors;
  }

  if (normalized.includes('early')) {
    return earlyDeliveryPillColors;
  }

  return neutralStatusPillColors;
};

export const buildStatusPillStyle = (
  colors: StatusPillColors,
  extra?: CSSProperties
): CSSProperties => ({
  backgroundColor: colors.backgroundColor,
  borderColor: colors.backgroundColor,
  color: colors.color,
  fontWeight: 600,
  ...extra,
});
